import React from 'react'
import { Tag, Tooltip } from 'antd'
import { WarningOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import moment from 'moment'
import { fetchHandoverProtocolList, HandoverProtocolQuery } from './handoverProtocolApi'
import { HandoverProtocolRow } from './types'

const LAST_N = 3

type Props = {
	title: string
	query: HandoverProtocolQuery | null
	onOpenPanel: () => void
	onOpenProtocol?: (id: number) => void
	// zobrazovat aj meno preberajuceho - v zakazke sa pracovnici menia, takze bez mena
	// sa neda povedat, kto co prebral
	showRecipient?: boolean
}

const HandoverProtocolSidebarSection = ({
	title, query, onOpenPanel, onOpenProtocol, showRecipient
}: Props) => {
	const { data, isLoading } = useQuery({
		queryKey: ['handoverProtocolSummary', query],
		queryFn: () => fetchHandoverProtocolList(query as HandoverProtocolQuery),
		enabled: !!query,
		staleTime: 30_000
	})

	const protocols: HandoverProtocolRow[] = data?.protocols ?? []
	const needsSignature = data?.summary?.needsSignature ?? false
	const lastItemChangeAt = data?.summary?.lastItemChangeAt

	const warningText = protocols.length === 0
		? 'Zamestnanec nemá podpísaný žiadny preberací protokol'
		: `Od posledného podpisu sa skladové položky zmenili${lastItemChangeAt ? ` (${moment(lastItemChangeAt).format('DD.MM.YYYY')})` : ''} — podpis už nesedí s aktuálnym stavom`

	return (
		<div className={'mt-5'}>
			<div
				style={{
					display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer'
				}}
				className={'font-bold'}
				onClick={onOpenPanel}
			>
				<span>{title}</span>
				{!isLoading && needsSignature && (
					<Tooltip title={warningText}>
						<WarningOutlined style={{ color: '#faad14' }} />
					</Tooltip>
				)}
			</div>

			{!isLoading && needsSignature && (
				<Tag color={'warning'} style={{ marginTop: 6, whiteSpace: 'normal' }}>
					{protocols.length === 0 ? 'Nepodpísaný' : 'Neaktuálny podpis'}
				</Tag>
			)}

			{protocols.slice(0, LAST_N).map((protocol) => (
				<div
					key={protocol.id}
					role={'button'}
					tabIndex={0}
					onClick={() => onOpenProtocol?.(protocol.id)}
					onKeyPress={() => onOpenProtocol?.(protocol.id)}
					style={{
						marginTop: 8, display: 'flex', justifyContent: 'space-between', gap: 8, cursor: 'pointer'
					}}
				>
					<span className={'mr-5'}>
						<Tag color={'green'}>Podpísaný</Tag>
						{showRecipient && <span>{protocol.recipientName}</span>}
					</span>
					<span>{moment(protocol.signedAt).format('DD.MM.YYYY')}</span>
				</div>
			))}

			{!isLoading && protocols.length === 0 && (
				<div className={'mt-2'}>Žiadne podpísané protokoly</div>
			)}
		</div>
	)
}

export default HandoverProtocolSidebarSection
